import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { combineLatestWith, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Buyer } from 'src/app/models/buyer.model';
import { BuyerService } from 'src/app/services/buyer.service';
import { CountriesService } from 'src/app/services/countries.service';
import { ToastrService } from 'ngx-toastr';
import { Country } from 'src/app/models/country.model';
import { LoadingService } from 'src/app/services/loading.service';
import * as intlTelInput from 'intl-tel-input';
import { telErrorMap, telUtilsScript } from 'src/app/dashboard/helpers/tel-errors';
@Component({
  selector: 'app-edit-buyer',
  templateUrl: './edit-buyer.component.html',
  styleUrls: ['./edit-buyer.component.scss'],
})
export class EditBuyerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() buyerForEditId!: string | null | undefined;
  @Input() companyId!: string;
  @Output() modalState = new EventEmitter<{
    isDelete: boolean;
    isUpdate: boolean;
  }>();
  @Output() newBuyer = new EventEmitter<Buyer>();
  textPattern = /^[\p{Letter}\p{N}\p{P}\p{S}\p{Zs}]+$/u;
  buyerForm = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.pattern(this.textPattern),
    ]),
    countryId: new FormControl('', [Validators.required]),
    city: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    address: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#'.,()\s-]+$/u),
    ]),
    province: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#'.,()\s-]+$/u),
    ]),
    zipCode: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{Letter}\p{Mark}0-9#.,()\s-]+$/u),
    ]),
    line1: new FormControl('', [Validators.pattern(this.textPattern)]),
    line2: new FormControl('', [Validators.pattern(this.textPattern)]),
    line3: new FormControl('', [Validators.pattern(this.textPattern)]),
    line4: new FormControl('', [Validators.pattern(this.textPattern)]),
    line5: new FormControl('', [Validators.pattern(this.textPattern)]),
    line6: new FormControl('', [Validators.pattern(this.textPattern)]),
    firstName: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    lastName: new FormControl('', [
      Validators.pattern(/^[\p{Letter}\p{Mark}'.,()\s-]+$/u),
    ]),
    email: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    phone: new FormControl('', [Validators.pattern(/^[0-9]+$/)]),
  });
  buyerForEdit!: Buyer;
  countries: Country[] = [];
  countriesForSearch: Country[] = [];
  searchCountries: FormControl = new FormControl('');
  userTelData: any;
  telError!: string;
  isTelCustomError!: boolean;
  private destroy = new Subject<boolean>();

  constructor(
    private buyerService: BuyerService,
    private countriesService: CountriesService,
    private toastr: ToastrService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.getCountries();
    this.searchCountries.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: (value: string) => {
        this.countries = this.countriesForSearch.filter((c: Country) =>
          c.name.toLowerCase().includes(value.toLowerCase()),
        );
      },
    });
    this.getBuyer();
  }

  ngAfterViewInit(): void {
    this.createPhoneElement();
  }

  listenFormChanges() {
    if (!this.buyerForm.controls.line1.value) {
      this.buyerForm.controls.name.valueChanges
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (value) => {
            this.buyerForm.controls.line1.patchValue(value);
          },
        });
    }

    if (!this.buyerForm.controls.line2.value) {
      this.buyerForm.controls.address.valueChanges
        .pipe(
          combineLatestWith(this.buyerForm.controls.city.valueChanges),
          takeUntil(this.destroy),
        )
        .subscribe({
          next: ([address, city]) => {
            this.buyerForm.controls.line2.patchValue(`${address}, ${city}`);
          },
        });
    }

    if (!this.buyerForm.controls.line3.value) {
      this.buyerForm.controls.province.valueChanges
        .pipe(
          combineLatestWith(
            this.buyerForm.controls.countryId.valueChanges,
            this.buyerForm.controls.zipCode.valueChanges,
          ),
          takeUntil(this.destroy),
        )
        .subscribe({
          next: ([province, countryId, zipCode]) => {
            this.buyerForm.controls.line3.patchValue(
              `${province}, ${
                this.getCountryById(countryId ?? '')?.name
              }, ${zipCode}`,
            );
          },
        });
    }
  }

  createPhoneElement() {
    const telInputEl = document.getElementById('phone');

    if (telInputEl) {
      this.userTelData = intlTelInput(telInputEl, {
        initialCountry: 'US',
        separateDialCode: true,
        utilsScript: telUtilsScript,
      });
    }
  }

  validateTel() {
    if (!this.userTelData.isPossibleNumber()) {
      this.isTelCustomError = true;
      this.telError = telErrorMap[this.userTelData.getValidationError()];

      if (!this.telError) {
        this.telError = 'Invalid number';
      }
    } else {
      this.isTelCustomError = false;
    }
  }

  getCountries() {
    this.countriesService.contriesList.pipe(takeUntil(this.destroy)).subscribe({
      next: (resp: Country[] | null) => {
        if (resp) {
          this.countries = resp.sort((a: Country, b: Country) => {
            return a.name.toLowerCase() < b.name.toLowerCase()
              ? -1
              : a.name.toLowerCase() > b.name.toLowerCase()
                ? 1
                : 0;
          });
          this.countriesForSearch = resp;
        }
      },
    });
  }

  getCountryById(id: string): Country | undefined {
    return this.countries.find((c) => c.id === id);
  }

  getBuyer() {
    if (this.buyerForEditId) {
      this.loadingService.changeLoadingStatus(true);
      this.buyerService
        .getBuyer(this.buyerForEditId)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: Buyer) => {
            this.buyerForEdit = resp;
            this.patchBuyerForm();
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.loadingService.changeLoadingStatus(false);
          },
        });
    } else {
      this.listenFormChanges();
    }
  }

  patchBuyerForm() {
    if (this.buyerForEdit) {
      this.buyerForm.patchValue({
        name: this.buyerForEdit.name,
        countryId: this.buyerForEdit.countryId,
        city: this.buyerForEdit.city,
        address: this.buyerForEdit.address,
        province: this.buyerForEdit.province,
        zipCode: this.buyerForEdit.zipCode,
        line1: this.buyerForEdit.line1
          ? this.buyerForEdit.line1
          : this.buyerForEdit.name,
        line2: this.buyerForEdit.line2
          ? this.buyerForEdit.line2
          : `${this.buyerForEdit.address}, ${this.buyerForEdit.city}`,
        line3: this.buyerForEdit.line3
          ? this.buyerForEdit.line3
          : `${this.buyerForEdit.province}, ${
              this.getCountryById(this.buyerForEdit.countryId)?.name
            }, ${this.buyerForEdit.zipCode}`,
        line4: this.buyerForEdit.line4,
        line5: this.buyerForEdit.line5,
        line6: this.buyerForEdit.line6,
        firstName: this.buyerForEdit.firstName,
        lastName: this.buyerForEdit.lastName,
        email: this.buyerForEdit.email,
      });
      if (this.buyerForEdit.phone) {
        this.userTelData.setNumber(this.buyerForEdit.phone);
      }
    }
  }

  deleteBuyer() {
    if (this.buyerForEdit && this.buyerForEdit.id) {
      this.loadingService.changeLoadingStatus(true);
      this.buyerService
        .deleteBuyer(this.buyerForEdit.id)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.toastr.success('Buyer successfully deleted', 'Success');
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.loadingService.changeLoadingStatus(false);
          },
        });
    }
  }

  addNewBuyer() {
    if (this.buyerForm.invalid) {
      this.buyerForm.markAllAsTouched();
      return;
    }
    this.loadingService.changeLoadingStatus(true);
    const payload: Partial<Buyer> = {
      name: this.buyerForm.controls.name.value ?? '',
      companyId: this.companyId,
      countryId: this.buyerForm.controls.countryId.value ?? '',
      city: this.buyerForm.controls.city.value ?? '',
      address: this.buyerForm.controls.address.value ?? '',
      province: this.buyerForm.controls.province.value ?? '',
      zipCode: this.buyerForm.controls.zipCode.value ?? '',
      line1: this.buyerForm.controls.line1.value
        ? this.buyerForm.controls.line1.value
        : (this.buyerForm.controls.name.value ?? ''),
      line2: this.buyerForm.controls.line2.value
        ? this.buyerForm.controls.line2.value
        : `${this.buyerForm.controls.address.value}, ${this.buyerForm.controls.city.value}`,
      line3: this.buyerForm.controls.line3.value
        ? this.buyerForm.controls.line3.value
        : `${this.buyerForm.controls.province.value}, ${
            this.getCountryById(
              this.buyerForm.controls.countryId.value || '',
            )?.name
          }, ${this.buyerForm.controls.zipCode.value}`,
      line4: this.buyerForm.controls.line4.value ?? '',
      line5: this.buyerForm.controls.line5.value ?? '',
      line6: this.buyerForm.controls.line6.value ?? '',
      firstName: this.buyerForm.controls.firstName.value ?? '',
      lastName: this.buyerForm.controls.lastName.value ?? '',
      email: this.buyerForm.controls.email.value ?? '',
      phone: this.userTelData.getNumber() ?? '',
    };
    if (!this.buyerForEdit) {
      this.buyerService
        .addBuyer(payload)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: Buyer) => {
            this.toastr.success('Buyer successfully added', 'Success');
            this.buyerForm.reset();
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.newBuyer.emit(resp);
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.modalState.emit({ isDelete: false, isUpdate: false });
            this.loadingService.changeLoadingStatus(false);
          },
        });
    } else {
      payload.id = this.buyerForEdit.id;
      this.buyerService
        .updateBuyer(this.buyerForEdit.id, payload)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: () => {
            this.toastr.success('Buyer successfully updated', 'Success');
            this.buyerForm.reset();
            this.modalState.emit({ isDelete: false, isUpdate: true });
            this.loadingService.changeLoadingStatus(false);
          },
          error: () => {
            this.modalState.emit({ isDelete: false, isUpdate: false });
            this.loadingService.changeLoadingStatus(false);
          },
        });
    }
  }

  closeModal() {
    this.modalState.emit({ isDelete: false, isUpdate: false });
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
